import { EventEmitter, Injectable } from '@angular/core';
import { LayerId, Milliseconds, Pixels, ZoomValue } from '../../types';
import {
  FIRST_LAYER_ID, TIMELINE_CANVAS_LEVEL_STEP, TIMELINE_DEFAULT_WIDTH, TIMELINE_DEFAULT_ZOOM, TIMELINE_MAX_ZOOM,
  TIMELINE_MIN_ZOOM
} from '../../constants';
import { Player } from '../entities/player';
import { RecorderStore } from '../stores/recorder.store';
import { RecordData } from '../interfaces/record-data';
import { PlaybackConfig } from '../interfaces/playback-config';
import { RecordingConfig } from '../interfaces/recording-config';

let instance: TimelineStore;

@Injectable()
export class TimelineStore {
  private _currentPosition: Milliseconds = 0;
  private _zoom: ZoomValue = TIMELINE_DEFAULT_ZOOM;
  private _width: Pixels = TIMELINE_DEFAULT_WIDTH;

  private playbackPreviousTime: number;
  private playbackRequestAnimationFrameId: number;

  public currentPositionChange: EventEmitter<Milliseconds> = new EventEmitter();
  public zoomChange: EventEmitter<ZoomValue> = new EventEmitter();
  public widthChange: EventEmitter<Pixels> = new EventEmitter();
  public change = new EventEmitter();

  public selectedLayerId: LayerId = FIRST_LAYER_ID;

  constructor(
    private player: Player,
    private recorderStore: RecorderStore,
  ) {
    if (instance) {
      return instance;
    } else {
      instance = this;
    }

    recorderStore.onSampleFinish
      .subscribe(sampleData => this.currentPosition = sampleData.endPosition);

    recorderStore.onRecordChange
      .subscribe(recordData => this.onRecordChange(recordData));

    player.onPlaybackStart
      .subscribe((playbackConfig) => this.onPlaybackStart(playbackConfig));

    player.onPlaybackStop
      .subscribe(() => this.onPlaybackStop());

    player.onPlaybackFinish
      .subscribe((playbackConfig) => this.onPlaybackFinish(playbackConfig));
  }

  private onRecordChange(recordData: RecordData) {
    if (recordData.layerIds.indexOf(this.selectedLayerId) === -1) {
      this.selectLastLayer();
    }
  }

  selectLastLayer() {
    this.selectedLayerId = this.recorderStore.lastLayerId;
  }

  private onPlaybackStart(playbackConfig: PlaybackConfig) {
    this.currentPosition = playbackConfig.startPosition;
    this.playbackPreviousTime = Date.now();

    this.requestPlaybackAnimationFrame();
  }

  private playbackAnimate() {
    const currentTime = Date.now();

    this.currentPosition += currentTime - this.playbackPreviousTime;
    this.playbackPreviousTime = currentTime;

    this.requestPlaybackAnimationFrame();
  }

  private requestPlaybackAnimationFrame() {
    this.playbackRequestAnimationFrameId = requestAnimationFrame(() => this.playbackAnimate());
  }

  private cancelPlaybackAnimationFrame() {
    cancelAnimationFrame(this.playbackRequestAnimationFrameId);
  }

  private onPlaybackStop() {
    this.cancelPlaybackAnimationFrame();
  }

  private onPlaybackFinish(playbackConfig: PlaybackConfig) {
    this.cancelPlaybackAnimationFrame();
    this.currentPosition = playbackConfig.endPosition;
  }

  isMaxZoom() {
    return this._zoom === TIMELINE_MAX_ZOOM;
  }

  isMinZoom() {
    return this._zoom === TIMELINE_MIN_ZOOM;
  }

  get step() {
    return this.convertPixelsToMilliseconds(TIMELINE_CANVAS_LEVEL_STEP);
  }

  set currentPosition(value) {
    if (value < 0) {
      value = 0;
    } else {
      value = Math.round(value);
    }

    if (this._currentPosition !== value) {
      this._currentPosition = value;
      this.currentPositionChange.emit(value);
      this.change.emit();
    }
  }

  get currentPosition(): Milliseconds {
    return this._currentPosition;
  }

  get recordingConfig(): RecordingConfig {
    return {
      layerId: this.selectedLayerId,
      position: this.currentPosition,
      step: this.step,
    };
  }

  set zoom(value) {
    if (value > TIMELINE_MAX_ZOOM) {
      value = TIMELINE_MAX_ZOOM;
    } else if (value < TIMELINE_MIN_ZOOM) {
      value = TIMELINE_MIN_ZOOM;
    }

    this._zoom = value;
    this.zoomChange.emit(value);
    this.change.emit();
  }

  get zoom(): ZoomValue {
    return this._zoom;
  }

  set width(value) {
    this._width = value;
    this.widthChange.emit(value);
    this.change.emit();
  }

  get width(): Pixels {
    return this._width;
  }

  get currentPositionInPixels(): Pixels {
    return this.convertMillisecondsToPixels(this.currentPosition);
  }

  get startVisiblePosition(): Milliseconds {
    return this.currentPosition - this.halfWidthInMilliseconds;
  }

  get startVisiblePositionInPixels(): Pixels {
    return this.convertMillisecondsToPixels(this.startVisiblePosition);
  }

  get endVisiblePosition(): Milliseconds {
    return this.currentPosition + this.halfWidthInMilliseconds;
  }

  get widthInMilliseconds(): Milliseconds {
    return this.convertPixelsToMilliseconds(this.width);
  }

  get halfWidthInMilliseconds(): Milliseconds {
    return this.convertPixelsToMilliseconds(this.halfWidth);
  }

  get halfWidth(): Pixels {
    return this.width / 2;
  }

  convertPixelsToMilliseconds(pixels: Pixels): Milliseconds {
    return pixels / this.zoom;
  }

  convertMillisecondsToPixels(milliseconds: Milliseconds): Pixels {
    return Math.round(milliseconds * this.zoom);
  }
}



// WEBPACK FOOTER //
// ./src/app/rec/timeline/timeline.store.ts